import React, { useMemo, useCallback, useState, useEffect } from 'react';
import styles from './SentencesSection.module.css';
import { LoaderIcon, HeartIcon, EditIcon, MicIcon } from '../../Icons';

const WORD_CHAR_REGEX = /[a-z0-9'’\-]/i;

const computePhraseHighlightRanges = (text, phrases) => {
  if (!text || !Array.isArray(phrases) || phrases.length === 0) {
    return [];
  }

  const seen = new Set();
  const normalized = [];

  phrases.forEach((raw) => {
    if (typeof raw !== 'string') {
      return;
    }
    const trimmed = raw.trim();
    if (!trimmed) {
      return;
    }
    const lower = trimmed.toLowerCase();
    if (seen.has(lower)) {
      return;
    }
    seen.add(lower);
    normalized.push({ original: trimmed, lower });
  });

  if (!normalized.length) {
    return [];
  }

  normalized.sort((a, b) => b.lower.length - a.lower.length);

  const lowerText = text.toLowerCase();
  const markers = new Array(lowerText.length).fill(false);
  const ranges = [];

  const isWordChar = (char) => WORD_CHAR_REGEX.test(char);

  normalized.forEach(({ lower }) => {
    let searchIndex = 0;
    while (searchIndex <= lowerText.length - lower.length) {
      const foundIndex = lowerText.indexOf(lower, searchIndex);
      if (foundIndex === -1) {
        break;
      }

      const matchEnd = foundIndex + lower.length;
      const beforeChar = foundIndex > 0 ? lowerText[foundIndex - 1] : '';
      const afterChar = matchEnd < lowerText.length ? lowerText[matchEnd] : '';
      const boundaryBefore = !isWordChar(beforeChar);
      const boundaryAfter = !isWordChar(afterChar);

      if (boundaryBefore && boundaryAfter) {
        let overlap = false;
        for (let index = foundIndex; index < matchEnd; index += 1) {
          if (markers[index]) {
            overlap = true;
            break;
          }
        }

        if (!overlap) {
          ranges.push({ start: foundIndex, end: matchEnd });
          for (let index = foundIndex; index < matchEnd; index += 1) {
            markers[index] = true;
          }
        }
      }

      searchIndex = foundIndex + lower.length;
    }
  });

  ranges.sort((a, b) => a.start - b.start);
  return ranges;
};

const SentencesSection = ({
  chapter,
  displayMode,
  chineseTextColor,
  englishTextColor,
  fontSize,
  lineHeight,
  sentenceFavorites,
  sentenceFavoriteLoading,
  sentenceNoteLoading,
  highlightSentenceId,
  backgroundColor,
  chapterWordSet,
  sentencePhraseFavorites = {},
  sentenceTimings = { en: {}, cn: {} },
  onSentenceFavorite,
  onSentenceNote,
  onWordClick,
  onSentenceClick,
  onSentencePronounce,
}) => {
  const allowWordClick = typeof onWordClick === 'function';
  const allowSentencePronounce = typeof onSentencePronounce === 'function';

  useEffect(() => {
    if (!chapterWordSet) {
      console.log('[SentencesSection] 未获取到收藏单词集');
      return;
    }

    if (typeof chapterWordSet.size === 'number' && typeof chapterWordSet.has === 'function') {
      console.log('[SentencesSection] 收藏单词(Set)', Array.from(chapterWordSet));
      return;
    }

    console.log('[SentencesSection] 收藏单词(非 Set)', chapterWordSet);
  }, [chapterWordSet]);

  const highlightFavoritedWords = useCallback(
    (text) => {
      if (
        !text ||
        !chapterWordSet ||
        typeof chapterWordSet.size !== 'number' ||
        typeof chapterWordSet.has !== 'function' ||
        chapterWordSet.size === 0
      ) {
        return text;
      }
      const parts = text.split(/(\b[\w'-]+\b)/);
      return parts.map((part, index) => {
        const normalized = part.replace(/[^A-Za-z'-]/g, '').toLowerCase();
        if (normalized && chapterWordSet.has(normalized)) {
          return (
            <span key={`favorited-${index}`} className={styles.favoritedWord}>
              {part}
            </span>
          );
        }
        return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
      });
    },
    [chapterWordSet]
  );

  const sentences = useMemo(() => {
    const originalTexts = [];
    const originalInfo = [];
    const translationTexts = [];
    const translationInfo = [];

    const pushEntry = (targetTexts, targetMeta, text, meta, fallbackIndex) => {
      const value = (text ?? '').toString();
      if (!value.trim()) {
        return;
      }
      const index = targetTexts.length;
      const metaObj = typeof meta === 'object' && meta !== null ? meta : {};
      const rawId =
        metaObj.sentence_id ??
        metaObj.id ??
        metaObj.sentenceId ??
        fallbackIndex ??
        index;
      const numericId = Number(rawId);
      const sentenceId = Number.isFinite(numericId) ? numericId : rawId ?? index;
      const orderIndex =
        metaObj.order_index ??
        metaObj.sentence_index ??
        fallbackIndex ??
        index;
      const startTime = metaObj.start_time ?? metaObj.start ?? null;

      targetTexts.push(value);
      targetMeta.push({
        sentenceId,
        orderIndex,
        startTime,
        raw: metaObj,
      });
    };

    const parseContentSource = (source, targetTexts, targetMeta) => {
      if (!source) return;
      try {
        const data = JSON.parse(source);
        if (Array.isArray(data)) {
          data.forEach((item, idx) => {
            if (typeof item === 'string') {
              pushEntry(targetTexts, targetMeta, item, { sentence_id: idx + 1 }, idx);
            } else if (item) {
              const text =
                item.sentence_content ??
                item.content ??
                '';
              pushEntry(targetTexts, targetMeta, text, item, idx);
            }
          });
        } else if (data?.sentences && Array.isArray(data.sentences)) {
          data.sentences.forEach((item, idx) => {
            const text =
              item?.sentence_content ??
              item?.content ??
              '';
            pushEntry(targetTexts, targetMeta, text, item, idx);
          });
        } else {
          const text =
            typeof data === 'string'
              ? data
              : data?.sentence_content ??
                data?.content ??
                '';
          pushEntry(targetTexts, targetMeta, text, data, 0);
        }
      } catch (err) {
        source
          .split(/\n+/)
          .forEach((text, idx) => {
            pushEntry(
              targetTexts,
              targetMeta,
              text,
              { sentence_id: idx + 1, order_index: idx },
              idx
            );
          });
      }
    };

    parseContentSource(chapter?.content, originalTexts, originalInfo);
    parseContentSource(chapter?.content_trans, translationTexts, translationInfo);

    if (originalTexts.length === 0 && translationTexts.length === 0) {
      if (chapter?.content && chapter.content.trim()) {
        return [
          {
            sentenceId: 0,
            orderIndex: 0,
            originalText: chapter.content,
            translatedText: '',
            startTime: null,
          },
        ];
      }
      return [];
    }

    const max = Math.max(originalTexts.length, translationTexts.length);
    const combined = [];

    for (let i = 0; i < max; i += 1) {
      const originalText = originalTexts[i] || '';
      const translatedText = translationTexts[i] || '';
      if (!originalText && !translatedText) {
        continue;
      }
      const originalMeta = originalInfo[i] || {};
      const translationMeta = translationInfo[i] || {};
      const sentenceId =
        originalMeta.sentenceId ??
        translationMeta.sentenceId ??
        i;
      const orderIndex =
        originalMeta.orderIndex ??
        originalMeta.sentence_index ??
        translationMeta.orderIndex ??
        translationMeta.sentence_index ??
        i;
      const startTime =
        originalMeta.startTime ??
        translationMeta.startTime ??
        null;

      combined.push({
        sentenceId,
        orderIndex,
        originalText,
        translatedText,
        startTime,
      });
    }

    return combined;
  }, [chapter]);

  const [activeSentenceId, setActiveSentenceId] = useState(null);

  const getMapValue = (map, key) => {
    if (
      map &&
      key !== undefined &&
      key !== null &&
      Object.prototype.hasOwnProperty.call(map, key)
    ) {
      return map[key];
    }
    return undefined;
  };

  const handleToggleActions = (sentenceId) => {
    setActiveSentenceId((prev) => (prev === sentenceId ? null : sentenceId));
  };

  const handleWordLookup = useCallback(
    (sentenceId, word) => {
      if (!allowWordClick || !word) return;
      onWordClick?.(word, sentenceId);
    },
    [allowWordClick, onWordClick]
  );

  if (!sentences.length) {
    return <div className={styles.noContent}>暂无章节内容</div>;
  }

  const createTextStyle = (baseColor) => ({
    color: baseColor,
    fontSize: typeof fontSize === 'number' ? `${fontSize}px` : undefined,
    lineHeight: typeof lineHeight === 'number' ? lineHeight : undefined,
  });

  const chineseTextStyle = createTextStyle(chineseTextColor);
  const englishTextStyle = createTextStyle(englishTextColor);

  return (
    <div className={styles.container}>
      {sentences.map((item, index) => {
        const { sentenceId, originalText, translatedText, startTime } = item;
        const resolvedId =
          sentenceId !== undefined && sentenceId !== null ? sentenceId : index;
        const sentenceKey = String(resolvedId);
        const favoriteValue = getMapValue(sentenceFavorites, resolvedId) ?? getMapValue(sentenceFavorites, sentenceKey);
        const isCurrentlyFavorited = favoriteValue === true;
        const isLoading =
          getMapValue(sentenceFavoriteLoading, sentenceKey) === true ||
          getMapValue(sentenceFavoriteLoading, resolvedId) === true;
        const noteLoading =
          getMapValue(sentenceNoteLoading, sentenceKey) === true ||
          getMapValue(sentenceNoteLoading, resolvedId) === true;
        const highlightKey =
          highlightSentenceId !== null && highlightSentenceId !== undefined
            ? String(highlightSentenceId)
            : null;
        const isHighlighted = highlightKey !== null && String(resolvedId) === highlightKey;
        const sentenceStyle = !isHighlighted ? { backgroundColor } : undefined;
        const showActions = activeSentenceId === resolvedId;
        const englishTiming =
          getMapValue(sentenceTimings?.en, resolvedId) ??
          getMapValue(sentenceTimings?.en, sentenceKey);
        const chineseTiming =
          getMapValue(sentenceTimings?.cn, resolvedId) ??
          getMapValue(sentenceTimings?.cn, sentenceKey);
        const fallbackTiming =
          startTime !== null && startTime !== undefined
            ? { start: startTime, end: null }
            : null;
        const favoriteTiming = {
          en: englishTiming || fallbackTiming,
          cn: chineseTiming || null,
        };
        const hasPronounceTiming =
          allowSentencePronounce &&
          ((favoriteTiming.en && favoriteTiming.en.start != null) ||
            (favoriteTiming.cn && favoriteTiming.cn.start != null));

        const handleSentenceGroupClick = () => {
          onSentenceClick?.(resolvedId, {
            originalText,
            translatedText,
            startTime,
            orderIndex: item.orderIndex ?? index,
          });
        };

        const handleChineseClick = (e) => {
          e.stopPropagation();
          handleToggleActions(resolvedId);
        };

        const handleWordClick = (word) => {
          if (!allowWordClick || !word) return;
          handleWordLookup(resolvedId, word.trim());
        };

        const handlePronounceClick = (event) => {
          if (!allowSentencePronounce) return;
          event.stopPropagation();
          onSentencePronounce?.(resolvedId, {
            timings: favoriteTiming,
            originalText,
            translatedText,
          });
        };

        const getWordEvents = (word) =>
          allowWordClick
            ? {
                onClick: (e) => {
                  e.stopPropagation();
                  handleWordClick(word);
                },
              }
            : {};

        const renderEnglishContent = (phraseList) => {
          if (!translatedText) {
            return null;
          }

          const displayText = translatedText.toString();
          const highlightRanges = computePhraseHighlightRanges(displayText, phraseList);
          
          if (!highlightRanges.length) {
            // 没有短语高亮，使用原来的逻辑（按单词分割）
            const parts = displayText.split(/(\s+)/);
            return parts.map((part, wordIndex) => {
              if (!part || /^\s+$/.test(part)) {
                return part;
              }

              if (part.trim()) {
                const normalized = part.replace(/[^A-Za-z'-]/g, '').toLowerCase();
                const isFavoritedWord =
                  normalized &&
                  chapterWordSet &&
                  typeof chapterWordSet.has === 'function' &&
                  chapterWordSet.has(normalized);

                const classNames = [styles.clickableWord];
                if (isFavoritedWord) {
                  classNames.push(styles.favoritedWord);
                }

                return (
                  <span
                    key={wordIndex}
                    className={classNames.join(' ')}
                    {...getWordEvents(part)}
                  >
                    {part}
                  </span>
                );
              }

              return part;
            });
          }

          // 有短语高亮，需要将文本分成片段
          const segments = [];
          let lastEnd = 0;

          highlightRanges.forEach((range, rangeIdx) => {
            // 添加高亮前的普通文本
            if (range.start > lastEnd) {
              segments.push({
                type: 'normal',
                text: displayText.substring(lastEnd, range.start),
                key: `normal-${rangeIdx}`,
              });
            }

            // 添加高亮短语
            segments.push({
              type: 'phrase',
              text: displayText.substring(range.start, range.end),
              key: `phrase-${rangeIdx}`,
            });

            lastEnd = range.end;
          });

          // 添加最后剩余的普通文本
          if (lastEnd < displayText.length) {
            segments.push({
              type: 'normal',
              text: displayText.substring(lastEnd),
              key: `normal-end`,
            });
          }

          // 渲染片段
          return segments.map((segment) => {
            if (segment.type === 'phrase') {
              // 短语作为整体渲染（连续波浪线）
              return (
                <span
                  key={segment.key}
                  className={`${styles.clickableWord} ${styles.favoritedPhrase}`}
                  onClick={(e) => {
                    if (!allowWordClick) return;
                    e.stopPropagation();
                    // 点击短语时，查询短语的第一个单词
                    const firstWord = segment.text.trim().split(/\s+/)[0];
                    if (firstWord) {
                      handleWordLookup(resolvedId, firstWord);
                    }
                  }}
                >
                  {segment.text}
                </span>
              );
            }

            // 普通文本按单词分割处理（支持单词收藏高亮）
            const parts = segment.text.split(/(\s+)/);
            return parts.map((part, partIdx) => {
              if (!part || /^\s+$/.test(part)) {
                return <React.Fragment key={`${segment.key}-${partIdx}`}>{part}</React.Fragment>;
              }

              if (part.trim()) {
                const normalized = part.replace(/[^A-Za-z'-]/g, '').toLowerCase();
                const isFavoritedWord =
                  normalized &&
                  chapterWordSet &&
                  typeof chapterWordSet.has === 'function' &&
                  chapterWordSet.has(normalized);

                const classNames = [styles.clickableWord];
                if (isFavoritedWord) {
                  classNames.push(styles.favoritedWord);
                }

                return (
                  <span
                    key={`${segment.key}-${partIdx}`}
                    className={classNames.join(' ')}
                    {...getWordEvents(part)}
                  >
                    {part}
                  </span>
                );
              }

              return <React.Fragment key={`${segment.key}-${partIdx}`}>{part}</React.Fragment>;
            });
          });
        };

        const resolvePhraseList = () => {
          if (!sentencePhraseFavorites || typeof sentencePhraseFavorites !== 'object') {
            console.log(`[SentencesSection] 句子 ${resolvedId}: 无收藏短语对象`);
            return [];
          }

          const candidates = [];
          if (resolvedId !== undefined && resolvedId !== null) {
            candidates.push(String(resolvedId));
          }
          candidates.push(String(index + 1));

          console.log(`[SentencesSection] 句子 ${resolvedId}: 候选键`, candidates);
          console.log(`[SentencesSection] 句子 ${resolvedId}: sentencePhraseFavorites`, sentencePhraseFavorites);

          for (const key of candidates) {
            if (
              key &&
              Object.prototype.hasOwnProperty.call(sentencePhraseFavorites, key) &&
              Array.isArray(sentencePhraseFavorites[key])
            ) {
              console.log(`[SentencesSection] 句子 ${resolvedId}: 找到短语`, sentencePhraseFavorites[key]);
              return sentencePhraseFavorites[key];
            }
          }

          console.log(`[SentencesSection] 句子 ${resolvedId}: 未找到匹配的短语`);
          return [];
        };

        const phraseListForSentence = resolvePhraseList();

        const englishParagraph =
          displayMode === 'zh-only'
            ? null
            : translatedText
              ? (
                  <p
                    key="en"
                    className={`${styles.sentenceText} ${styles.translatedText}`}
                    style={englishTextStyle}
                  >
                    {renderEnglishContent(phraseListForSentence)}
                  </p>
                )
              : originalText
                ? (
                    <p
                      key="en"
                      className={`${styles.sentenceText} ${styles.noTranslation}`}
                      style={englishTextStyle}
                    >
                      <em>暂无翻译</em>
                    </p>
                  )
                : null;

        const chineseParagraph =
          displayMode !== 'en-only' && originalText
            ? (
                <p
                  key="zh"
                  className={`${styles.sentenceText} ${styles.originalText}`}
                  style={chineseTextStyle}
                  onClick={handleChineseClick}
                >
                  {highlightFavoritedWords(originalText.trim())}
                </p>
              )
            : null;

        const renderedParagraphs = (
          displayMode === 'en-zh' || displayMode === 'en-only'
            ? [englishParagraph, chineseParagraph]
            : [chineseParagraph, englishParagraph]
        ).filter(Boolean);

        const renderExpandedContent = () => (
          <div className={styles.sentenceExpandedRow}>
            <div className={styles.actionButtons}>
              <button
                className={`${styles.favoriteButton} ${
                  isCurrentlyFavorited ? styles.favoriteButtonActive : ''
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  onSentenceFavorite?.(
                    resolvedId,
                    originalText,
                    translatedText,
                    favoriteTiming
                  );
                }}
                disabled={isLoading}
                title={isCurrentlyFavorited ? '取消收藏句子' : '收藏句子'}
                type="button"
                aria-label={isCurrentlyFavorited ? '取消收藏句子' : '收藏句子'}
              >
                {isLoading ? (
                  <LoaderIcon size={20} />
                ) : (
                  <HeartIcon size={20} filled={isCurrentlyFavorited} />
                )}
              </button>
              <button
                className={styles.noteButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onSentenceNote?.(
                    resolvedId,
                    originalText,
                    translatedText,
                    item.orderIndex ?? index
                  );
                }}
                disabled={noteLoading}
                title="添加笔记"
                type="button"
              >
                {noteLoading ? <LoaderIcon size={20} /> : <EditIcon size={20} />}
              </button>
              {allowSentencePronounce && (
                <button
                  className={styles.pronounceButton}
                  onClick={handlePronounceClick}
                  title="播放发音"
                  type="button"
                  disabled={!hasPronounceTiming}
                >
                  <MicIcon size={20} />
                </button>
              )}
            </div>
            <div className={styles.sentenceContent}>
              {renderedParagraphs}
            </div>
          </div>
        );

        const renderCollapsedContent = () => (
          <div className={`${styles.sentenceRow} ${styles.collapsedRow}`}>
            {renderedParagraphs}
          </div>
        );

        const groupClassName = [
          styles.sentenceGroup,
          isHighlighted ? styles.highlighted : '',
          styles.clickableSentence,
          showActions ? styles.active : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <div
            key={sentenceKey}
            id={`sentence-${resolvedId}`}
            className={groupClassName}
            style={sentenceStyle}
            onClick={handleSentenceGroupClick}
            role={onSentenceClick ? 'button' : 'presentation'}
            tabIndex={onSentenceClick ? 0 : -1}
          >
            {showActions ? renderExpandedContent() : renderCollapsedContent()}
          </div>
        );
      })}
    </div>
  );
};

export default SentencesSection;
